import React, {ReactNode} from 'react'
import {Alert} from 'antd'
import {GenerateDatasetPeriodLabel} from './DateRange'

export enum ResultStatus {
    DisplayCapacityExceeded = 'Display Capacity',
    ResultsCapacityExceeded = 'Results Capacity',
    ZeroResultsReturned = 'ZeroResultsReturned',
    QueryCancelled = 'Query Cancelled',
    ServerError = 'ServerError',
    SavedResultsExpired = 'SavedResultsExpired'
}

interface ResultStatusMessageProps {
    queryId: string
    resultStatus: ResultStatus
    message?: string | ReactNode
    haveTimeFrameFiltersExpired?: boolean
}

const generateSystemCapacityStatusMessage = (resultStatus: ResultStatus) => {

    const systemCapacityMessage = <div>
        <p>
            <strong>{resultStatus} Exceeded.</strong> It seems your query has hit
            the {resultStatus.toLowerCase()} capacity limit in Vantage.
            Don’t worry, we’ve got some tips to help you out...
        </p>
        <ul>
            <li>
                <strong>Add More Filters</strong>: Try specifying a financial year or a locality to refine your query.
            </li>
            <li>
                <strong>Be Specific</strong>: Choose specific data points rather than querying everything.
            </li>
        </ul>

    </div>

    let systemCapacityMessageFooter = <> </>;

    if (resultStatus === ResultStatus.DisplayCapacityExceeded) {
        systemCapacityMessageFooter =
            <div>Alternatively, you can <strong>‘Export’</strong> the results and be notified by
                <strong> email</strong> when the results are ready to be retrieved from the
                <strong> ‘Query Progress List’</strong> page.
            </div>
    } else if (resultStatus === ResultStatus.ResultsCapacityExceeded) {
        systemCapacityMessageFooter =
            <div>If you’re still having trouble, please contact the support desk for further guidance.
                You can find this by clicking on the <strong> ‘Contact Us’ </strong>
                button on the top right hand corner of Vantage.
            </div>
    }

    return <>
        {systemCapacityMessage}
        {systemCapacityMessageFooter}
    </>

}

const generateResultStatusMessage = (resultStatus: ResultStatus, areTimeFrameFiltersOutdated?: boolean, queryId?: string) => {

    if (resultStatus === ResultStatus.ResultsCapacityExceeded || resultStatus === ResultStatus.DisplayCapacityExceeded) {
        return generateSystemCapacityStatusMessage(resultStatus)
    }

    if (resultStatus === ResultStatus.ZeroResultsReturned) {
        return <div>
            0 results returned for this query.
        </div>
    }

    if (resultStatus === ResultStatus.SavedResultsExpired) {
        return <div>
            Saved Query results are deleted periodically as revisions
            may be added to historical data with each Vantage update. <br/><br/>
            Check your period of study, and please run your query again to view the most up-to-date results. <br/><br/>

            {areTimeFrameFiltersOutdated ? <strong>{GenerateDatasetPeriodLabel()}</strong> : null}
        </div>
    }

    if (resultStatus === ResultStatus.ServerError) {
        return <div>
            An error occurred whilst calculating your results. <br/><br/>
            Please try running your query again. <br/><br/>
            <strong>Query ID: {queryId}</strong>
        </div>
    }

    if (resultStatus === ResultStatus.QueryCancelled) {
        return <div>
            The query has been cancelled.
        </div>
    }

}

const ResultStatusMessage = ({queryId, resultStatus, haveTimeFrameFiltersExpired}: ResultStatusMessageProps) => {

    const resultStatusMessage = generateResultStatusMessage(resultStatus, haveTimeFrameFiltersExpired, queryId)

    return <div data-testid='message'>
        <Alert
            message={!(resultStatus === ResultStatus.ZeroResultsReturned || resultStatus === ResultStatus.QueryCancelled) ?
                <strong>Oops!</strong> : <></>}
            description={resultStatusMessage}
            type="warning"
            showIcon
        />
    </div>

}

export default ResultStatusMessage